import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { LoaderCircle, Plus, Unlink } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { submittalStatusVariant } from '@/lib/status-variants'
import type { CostContractSubmittal } from '../types'
import SubmittalPickerDialog from './SubmittalPickerDialog'

interface Props {
  projectId: string
  contractId: string
  submittals: CostContractSubmittal[]
  canEdit: boolean
  /** Resolve true when the server accepted the change. */
  onLink: (ids: string[]) => Promise<boolean>
  onUnlink: (submittalId: string) => Promise<boolean>
}

/** "Submittaly" (integrations spec §2.5): the approvals this contract's scope waits on. Read-only for a representative. */
const ContractSubmittalsCard: React.FC<Props> = ({ projectId, contractId, submittals, canEdit, onLink, onUnlink }) => {
  const { t } = useTranslation()
  const [picking, setPicking] = useState(false)
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null)
  const unlink = async (id: string) => {
    setUnlinkingId(id)
    await onUnlink(id)
    setUnlinkingId(null)
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_submittals_title')}</CardTitle>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
            <Plus className="h-4 w-4" />
            {t('CostControl_submittals_link')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {submittals.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_submittals_empty')}</p>}
        {submittals.length > 0 && (
          <ul className="space-y-2">
            {submittals.map((s) => (
              <li key={s.id} className="flex items-center gap-2 rounded border px-2.5 py-2 text-sm">
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{s.displayNumber}</span>
                {s.canOpen
                  ? <Link to={`/project/${projectId}/submittals/${s.id}`} className="min-w-0 flex-1 truncate text-primary hover:underline" title={s.title}>{s.title}</Link>
                  : <span className="min-w-0 flex-1 truncate" title={s.title}>{s.title}</span>}
                {s.pending && <Badge variant="warning" className="shrink-0">{t('CostControl_submittals_pending_marker')}</Badge>}
                <Badge variant={submittalStatusVariant(s.status)} className="shrink-0">{t(`Submittal_status_${s.status}`)}</Badge>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => unlink(s.id)}
                    disabled={unlinkingId === s.id}
                    aria-label={t('CostControl_submittals_unlink')}
                  >
                    {unlinkingId === s.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {canEdit && <SubmittalPickerDialog open={picking} projectId={projectId} contractId={contractId} onOpenChange={setPicking} onLink={onLink} />}
    </Card>
  )
}
export default ContractSubmittalsCard
